import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * A BHT order. Note what is absent: no free-text field.
 *
 * An order concerns an identified person's health, and free text is where the
 * clinical detail collects — then travelling as ordinary e-mail into a
 * ticketing system. The case handler asks for the detail when they make
 * contact. Do not add a notes or message field back without re-taking that
 * decision; the backend has no field to receive one.
 */
export interface SubmitBhtOrderBody {
  categoryKey: string;
  /** Omitted for the catch-all category, which has no service list. */
  serviceKey?: string | null;
  /** The person the service is for — forwarded in the order e-mail, never stored. */
  contactName: string;
  contactEmail: string;
  contactPhone: string;
}

export interface SubmitBhtOrderResult {
  /**
   * Customer-facing reference, e.g. "BHT-2026-4871". The only thing Kuba keeps,
   * and the same string both order e-mails print — the receipt showing a bare
   * row id while the e-mails showed a formatted reference is how a customer
   * ends up quoting a number the case handler cannot find.
   */
  reference: string;
  /** True when the confirmation e-mail to the person who ordered went out. */
  confirmationSent: boolean;
}

/**
 * Submits the Flexi Booking portal form. The backend resolves the catalogue
 * keys to their Norwegian labels, e-mails the order to the recipients
 * configured for the business's application, and records only that an order
 * was placed.
 */
@Injectable({ providedIn: 'root' })
export class BookingPortalService {
  private readonly base = `${environment.BASE_URL}/flexi/customer-request`;

  constructor(private http: HttpClient) {}

  /**
   * Whether the current user may place a BHT order for this business.
   *
   * Any failure resolves to false: "we could not establish access" and "not
   * allowed" should both leave the tab hidden. Ordering is restricted to an
   * administrator, the business's HSE adviser and its OHS responsible, so an
   * Editor must not be shown a tab that would refuse them on submit.
   */
  canOrder(businessId: number): Observable<boolean> {
    return this.http
      .get<boolean>(`${this.base}/${businessId}/bht-order/access`)
      .pipe(map(canOrder => !!canOrder), catchError(() => of(false)));
  }

  submit(businessId: number, body: SubmitBhtOrderBody): Observable<SubmitBhtOrderResult> {
    return this.http.post<any>(`${this.base}/${businessId}/bht-order`, body).pipe(
      map(r => ({
        // Falls back to the raw row id only if an older backend answers without
        // a formatted reference; the receipt is never left blank.
        reference: String(r?.reference ?? r?.Reference ?? r?.requestId ?? r?.RequestId ?? ''),
        // The backend reuses the quote-request response shape, where this field
        // is named for the advisor copy it carries there.
        confirmationSent: r?.advisorNotified ?? r?.AdvisorNotified ?? false,
      })),
    );
  }
}
